import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import "../pd-popup-dialog.js";
import { LitElement, css, html } from "lit";
import { property } from "lit/decorators.js";
//#region src/pd-confirm-dialog/PdConfirmDialog.ts
/**
* PdConfirmDialog - Abstract base class for confirmation dialogs
*
* Provides a consistent pattern for confirmation dialogs with:
* - Configurable OK/Cancel buttons (or single button mode)
* - Typed dialog icons (info, warn, error, help)
* - Standardized event emission (confirm/cancel)
*
* @example
* ```typescript
* @customElement("my-confirm-dialog")
* export class MyConfirmDialog extends PdConfirmDialog {
*   protected _dialogTitle = "Confirm Action";
*   protected _dialogType = "warn";
*   protected _singleButton = false;
*
*   protected _renderContent() {
*     return html`<p>Are you sure you want to proceed?</p>`;
*   }
* }
* ```
*/
/** Button key for cancel/abort action */
var BUTTON_KEY_CANCEL = "cancel";
/** Button key for confirm/ok action */
var BUTTON_KEY_CONFIRM = "confirm";
/** Event name fired when dialog is confirmed */
var EVENT_CONFIRM = "pd-dialog-confirm";
/** Event name fired when dialog is cancelled */
var EVENT_CANCEL = "pd-dialog-cancel";
var PdConfirmDialog = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.confirmButtonText = "Ok";
		this.cancelButtonText = "Cancel";
		this.closeByEscape = false;
		this.closeByBackdrop = false;
	}
	static {
		this.styles = css`
    :host {
      display: block;
      --pd-popup-max-width: var(--pd-confirm-dialog-max-width, 600px);
    }

    @media (max-width: 767px) {
      :host {
        --pd-popup-max-width: 90%;
      }
    }
  `;
	}
	render() {
		return html`
      <pd-popup-dialog
        @submit-button-clicked="${this._handleButtonClick}"
        title="${this._dialogTitle}"
        .buttons="${this._generateButtons()}"
        type="${this._dialogType}"
        ?closeByEscape="${this.closeByEscape}"
        ?closeByBackdrop="${this.closeByBackdrop}"
      >
        <div slot="content">${this._renderContent()}</div>
      </pd-popup-dialog>
    `;
	}
	/**
	* Handle button click events.
	* Override to customize behavior.
	*/
	_handleButtonClick(e) {
		const isConfirm = e.detail.button === BUTTON_KEY_CONFIRM;
		this.dispatchEvent(new CustomEvent(isConfirm ? EVENT_CONFIRM : EVENT_CANCEL, {
			bubbles: true,
			composed: true
		}));
	}
	_generateButtons() {
		if (this._singleButton) return [{
			key: BUTTON_KEY_CONFIRM,
			name: this.confirmButtonText,
			primary: true
		}];
		return [{
			key: BUTTON_KEY_CANCEL,
			name: this.cancelButtonText
		}, {
			key: BUTTON_KEY_CONFIRM,
			name: this.confirmButtonText,
			primary: true
		}];
	}
};
__decorate([property({ type: String })], PdConfirmDialog.prototype, "confirmButtonText", void 0);
__decorate([property({ type: String })], PdConfirmDialog.prototype, "cancelButtonText", void 0);
__decorate([property({ type: Boolean })], PdConfirmDialog.prototype, "closeByEscape", void 0);
__decorate([property({ type: Boolean })], PdConfirmDialog.prototype, "closeByBackdrop", void 0);
//#endregion
export { BUTTON_KEY_CANCEL, BUTTON_KEY_CONFIRM, EVENT_CANCEL, EVENT_CONFIRM, PdConfirmDialog };
